export class PointVector2D {
    x: number;
    y: number;

    constructor(x: number, y: number) {
        this.x = x;
        this.y = y;
    }

    copy() {
        return new PointVector2D(this.x, this.y)
    }

    get magnitude() {
        return Math.sqrt(this.x ** 2 + this.y ** 2);
    }

    get angle() {
        const angleClockwise = this.angleToClockwise(new PointVector2D(1, 0));
        if (angleClockwise > 180) return angleClockwise - 360; 
        return angleClockwise;
    }

    determinant(other: PointVector2D) {
        return this.x * other.y - this.y * other.x;
    }

    angleTo(other: PointVector2D) {
        return Math.acos(this.dot(other) / (this.magnitude * other.magnitude)) * (180 / Math.PI);
    }

    angleToClockwise(other: PointVector2D) {
        const inner = this.angleTo(other);
        const det = this.determinant(other);
        if (det <= 0) return inner;
        else return 360 - inner;
    }

    distanceTo(other: PointVector2D) {
        return Math.sqrt((this.x - other.x) ** 2 + (this.y - other.y) ** 2)
    }

    add(other: PointVector2D) {
        this.x = this.x + other.x;
        this.y = this.y + other.y;
        return this;
    }

    subtract(other: PointVector2D) {
        this.x = this.x - other.x;
        this.y = this.y - other.y;
        return this;
    }

    multiply(scalar: number) {
        this.x = this.x * scalar;
        this.y = this.y * scalar;
        return this;
    }

    set(x: number, y: number) {
        this.x = x;
        this.y = y;
        return this;
    }

    orient(angle: number) {
        angle = angle % 360;
        if (angle > 180) angle -= 360;
        else if (angle < -180) angle += 360;
        const currentAngle = this.angle;
        const rotationAngle = angle - currentAngle;
        this.rotate(rotationAngle);
        return this;
    }

    setMagnitude(magnitude: number) {
        return this.normalize().multiply(magnitude);
    }

    normalize() {
        const d = this.magnitude;
        if (d != 0) {
            this.x = this.x / d;
            this.y = this.y / d;
        }
        return this;
    }

    rotate(angle: number) {
        angle = angle * (Math.PI / 180);
        const cos_a = Math.cos(angle);
        const sin_a = Math.sin(angle);
        const qx = cos_a * this.x - sin_a * this.y;
        const qy = sin_a * this.x + cos_a * this.y;
        this.x = qx;
        this.y = qy;
        return this;
    }

    rotateFromOrigin(angle: number, origin: PointVector2D) {
        const originNegative = origin.copy();
        originNegative.reverse();
        this.add(originNegative);
        this.rotate(angle);
        this.add(origin);
        return this;
    }

    reverse() {
        this.x = -this.x;
        this.y = -this.y;
        return this;
    }

    dot(other: PointVector2D) {
        return this.x * other.x + this.y * other.y
    }

    cross() {
        return new PointVector2D(this.y, -this.x)
    }
}

export class PointVector3D {
    x: number;
    y: number;
    z: number;

    constructor(x: number, y: number, z: number = 0) {
        this.x = x;
        this.y = y;
        this.z = z;
    }

    static fromPointVector2D(pointVector: PointVector2D) {
        return new PointVector3D(pointVector.x, pointVector.y);
    }

    get magnitude() {
        return Math.sqrt(this.x ** 2 + this.y ** 2 + this.z ** 2);
    }

    add(other: PointVector3D) {
        this.x = this.x + other.x;
        this.y = this.y + other.y;
        this.z = this.z + other.z;
        return this;
    }

    subtract(other: PointVector3D) {
        this.x = this.x - other.x;
        this.y = this.y - other.y;
        this.z = this.z - other.z;
        return this;
    }

    set(x: number, y: number, z: number) {
        this.x = x;
        this.y = y;
        this.z = z;
        return this;
    }
    
    setToZero() {
        this.x = 0;
        this.y = 0;
        this.z = 0;
        return this;
    }

    match(other: PointVector3D) {
        this.x = other.x;
        this.y = other.y;
        this.z = other.z;
        return this;
    }

    copy() {
        return new PointVector3D(this.x, this.y, this.z);
    }

    reverse() {
        this.x = -this.x;
        this.y = -this.y;
        this.z = -this.z;
        return this;
    }

    cross(other: PointVector3D) {
        return this.set
        (
            this.y * other.z - other.y * this.z,
            other.x * this.z - this.x * other.z,
            this.x * other.y - other.x * this.y
        );
    }

    multiply(scalar: number) {
        this.x = this.x * scalar;
        this.y = this.y * scalar;
        this.z = this.z * scalar;
        return this;
    }

    divide(scalar: number) {
        this.x = this.x / scalar;
        this.y = this.y / scalar;
        this.z = this.z / scalar;
        return this;
    }

    dot(other: PointVector3D) {
        return this.x * other.x + this.y * other.y + this.z * other.z;
    }

    angle(other: PointVector3D) {
        return Math.acos(this.dot(other) / (this.magnitude * other.magnitude));
    }

    normalize() {
        const d = this.magnitude;
        if (d != 0) this.divide(this.magnitude);
        return this;
    }

    rotate(axis: PointVector3D, angle: number, origin?: PointVector3D) {
        if (origin === undefined) {origin = new PointVector3D(0,0,0);}
        const x = this.x - origin.x;
        const y = this.y - origin.y;
        const z = this.z - origin.z;
        const u = axis.x;
        const v = axis.y;
        const w = axis.z;
        const r2 = Math.pow(u,2) + Math.pow(v,2) + Math.pow(w,2);
        const r = Math.sqrt(r2)
        const ct = Math.cos(angle)
        const st = Math.sin(angle) / r
        const dt = (u * x + v * y + w * z) * (1 - ct) / r2
        return this.set((u * dt + x * ct + (-w * y + v * z) * st) + origin.x,
                        (v * dt + y * ct + (w * x - u * z) * st)  + origin.y,
                        (w * dt + z * ct + (-v * x + u * y) * st) + origin.z)
    }

    closestPoint(point: PointVector3D, origin: PointVector3D) {
        const r = origin;
        const t = this;
        const a = point;
        const x = (t.x * (a.x - r.x) + t.y * (a.y - r.y) + t.z * (a.z - r.z)) / (Math.pow(t.x,2) + Math.pow(t.y,2) + Math.pow(t.z,2))
        return new PointVector3D(r.x + t.x * x, r.y + t.y * x, r.z + t.z * x);
    }

    dist(point: PointVector3D) {
        return Math.sqrt((Math.pow(this.z-point.z,2)+Math.pow(this.x-point.x,2))+Math.pow(this.y-point.y,2));
    }
}

export class Plane3D {
    normal: PointVector3D;
    origin: PointVector3D;
    xAxis: PointVector3D;
    yAxis: PointVector3D;
    constructor(normal: PointVector3D, origin: PointVector3D, xAxis?: PointVector3D) {
        const createXAxis = () => {
            if (this.normal.x == 0 && this.normal.y == 0) {
                this.xAxis = new PointVector3D(1, 0, 0);
            }
            else {
                this.xAxis = new PointVector3D(normal.y, -normal.x, 0);
                this.xAxis = this.xAxis.normalize();
            }
        }
        this.normal = normal.normalize();
        if (!xAxis) {
            this.xAxis = this.normal;
            createXAxis();
        }
        else if (Math.abs(this.normal.x * xAxis.x + this.normal.y * xAxis.y + this.normal.z * xAxis.z) < 0.01) {this.xAxis = xAxis.normalize();}
        else {
            this.xAxis = this.normal;
            createXAxis();
        }
        this.origin = origin;
        this.yAxis = this.normal.copy();
        this.yAxis.cross(this.xAxis);
    }

    xyToXyz(vector2d: PointVector2D | PointVector3D) {
        const u = [this.xAxis.x * vector2d.x, this.xAxis.y * vector2d.x, this.xAxis.z * vector2d.x]
        const v = [this.yAxis.x * vector2d.y, this.yAxis.y * vector2d.y, this.yAxis.z * vector2d.y]
        return new PointVector3D(this.origin.x + u[0] + v[0], this.origin.y + u[1] + v[1], this.origin.z + u[2] + v[2])
    }

    rotate(axis: PointVector3D, angle: number, origin: PointVector3D) {
        this.normal.rotate(axis, angle);
        this.origin.rotate(axis, angle, origin);
        this.xAxis.rotate(axis, angle);
        this.yAxis.rotate(axis, angle);
    }
}

export class Circle3D {
    plane: Plane3D;
    center: PointVector3D;
    radius: number;

    constructor(plane: Plane3D, radius: number) {
        this.plane = plane;
        this.center = this.plane.origin;
        this.radius = radius;
    }

    pointAt(parameter: number) {
        const ang = 2 * Math.PI * parameter;
        const xyCoord = new PointVector3D(Math.cos(ang) * this.radius, Math.sin(ang) * this.radius)
        return this.plane.xyToXyz(xyCoord)
    }

    subdivide(number: number) {
        const interval = 1 / number;
        let parameter = 0;
        const subPts = [];
        while (parameter < 1) {
            subPts.push(this.pointAt(parameter));
            parameter += interval;
        }
        return subPts
    }
}

export class Vertex {
    position: PointVector3D;
    edgeIndices: Array<number>;
    faceIndices: Array<number>;

    constructor(position: PointVector3D) {
        this.position = position;
        this.edgeIndices = [];
        this.faceIndices = [];
    }

    static fromPositions(positions: Array<PointVector3D>) {
        const returnValues = [];
        for (let i = 0; i < positions.length; i++) {
            const position = positions[i];
            const vert = new Vertex(position);
            returnValues.push(vert);
        }
        return returnValues;
    }

    rotate(axis: PointVector3D, angle: number, origin: PointVector3D) {
        this.position.rotate(axis, angle, origin);
    }
}

export class Face {
    vertexIndices: Array<number>;
    edgeIndices: Array<number>;

    constructor(vertexIndices: Array<number>) {
        this.vertexIndices = vertexIndices;
        this.edgeIndices = [];
    }
}

export class Edge {
    vertexIndices: Array<number>;
    faceIndices: Array<number>;
    
    constructor(vertexIndices: Array<number>) {
        this.vertexIndices = vertexIndices;
        this.faceIndices = [];
    }

    midpoint(mesh: Mesh) {
        return mesh.vertices[this.vertexIndices[0]].position.copy().add(mesh.vertices[this.vertexIndices[1]].position).divide(2);
    }

    vertexPositions(mesh: Mesh) {
        return [
            mesh.vertices[this.vertexIndices[0]].position,
            mesh.vertices[this.vertexIndices[1]].position,
        ];
    }

    normal(mesh: Mesh) {
        if (!mesh.faceNormals || !mesh.faceAreas) {
            mesh.calculateFaceAreasAndNormals();
        }
        const edgeNormal = mesh.faceNormals![this.faceIndices[0]].copy();
        edgeNormal.add(mesh.faceNormals![this.faceIndices[1]]);
        edgeNormal.normalize();
        return edgeNormal;
    }
}

export class Mesh {
    vertices: Array<Vertex>;
    faces: Array<Face>;
    edges: Array<Edge>;
    faceNormals: Array<PointVector3D> | undefined;
    faceAreas: Array<number> | undefined;
    faceCentroids: Array<PointVector3D> | undefined;
    vertexNormals: Array<PointVector3D> | undefined;
    min: PointVector3D | undefined;
    max: PointVector3D | undefined;
    center: PointVector3D | undefined;

    sortVector: PointVector3D | undefined;
    sortVectorOrigin: PointVector3D | undefined;
    sortVectorClosestPoints: Array<PointVector3D> | undefined;
    sortedVertices: Array<number> | undefined;
    sortedDistancies: Array<number> | undefined;

    constructor(vertices: Array<Vertex>, faces: Array<Face>) {
        this.vertices = vertices;
        this.faces = faces;
        this.edges = [];
        const vertexIndicesMinMaxToEdgeIndexLookup: Array<Array<number>> = [];
        for (let f = 0; f < this.faces.length; f++) {
            const face = this.faces[f];
            const vertexIndicesForFace = face.vertexIndices;
            const numberOfVerticesInFace = vertexIndicesForFace.length; 
            for (let vi = 0; vi < numberOfVerticesInFace; vi++) {
                const viNext = (vi + 1) % numberOfVerticesInFace;
                const vertexIndex = vertexIndicesForFace[vi];
                const vertexIndexNext = vertexIndicesForFace[viNext];
                const vert = this.vertices[vertexIndex];
                vert.faceIndices.push(f);
                const vertexIndexMin = Math.min(vertexIndex, vertexIndexNext);
                const vertexIndexMax = Math.max(vertexIndex, vertexIndexNext);
                let vertexIndexMaxToEdgeIndexLookup = vertexIndicesMinMaxToEdgeIndexLookup[vertexIndexMin];
                if (vertexIndexMaxToEdgeIndexLookup == null) {
                    vertexIndexMaxToEdgeIndexLookup = [];
                    vertexIndicesMinMaxToEdgeIndexLookup[vertexIndexMin] = vertexIndexMaxToEdgeIndexLookup;
                }
                let edgeIndex = vertexIndexMaxToEdgeIndexLookup[vertexIndexMax];
                if (edgeIndex == null) {
                    const edge = new Edge([vertexIndexMin, vertexIndexMax]);
                    edgeIndex = this.edges.length;
                    this.edges.push(edge);
                }
                vertexIndexMaxToEdgeIndexLookup[vertexIndexMax] = edgeIndex;
                if (face.edgeIndices.indexOf(edgeIndex) == -1) {
                    face.edgeIndices.push(edgeIndex);
                }
            }
            for (let ei = 0; ei < face.edgeIndices.length; ei++) {
                const edgeIndex = face.edgeIndices[ei];
                const edge = this.edges[edgeIndex];
                edge.faceIndices.push(f);
                for (let vi = 0; vi < edge.vertexIndices.length; vi++) {
                    const vertexIndex = edge.vertexIndices[vi];
                    const vert = this.vertices[vertexIndex];
                    if (vert.edgeIndices.indexOf(edgeIndex) == -1) {
                        vert.edgeIndices.push(edgeIndex);
                    }
                }
            }
        }
    }

    calculateMinMax() {
        const min_pt = [this.vertices[0].position.x, this.vertices[0].position.y, this.vertices[0].position.z]
        const max_pt = [this.vertices[0].position.x, this.vertices[0].position.y, this.vertices[0].position.z]
        for (let v = 1; v < this.vertices.length; v++) {
            const vert = this.vertices[v].position;
            if (vert.x < min_pt[0]) {min_pt[0] = vert.x}
            else if (vert.x > max_pt[0]) {max_pt[0] = vert.x}
            if (vert.y < min_pt[1]) {min_pt[1] = vert.y}
            else if (vert.y > max_pt[1]) {max_pt[1] = vert.y}
            if (vert.z < min_pt[2]) {min_pt[2] = vert.z}
            else if (vert.z > max_pt[2]) {max_pt[2] = vert.z}
        }
        this.min = new PointVector3D(min_pt[0], min_pt[1], min_pt[2])
        this.max = new PointVector3D(max_pt[0], max_pt[1], max_pt[2])
        return [this.min, this.max]
    }

    calculateCenter() {
        if (!this.center) {
            if (!this.min || !this.max) {
                this.calculateMinMax();
            }
            this.center = new PointVector3D((this.min!.x + this.max!.x) / 2, (this.min!.y + this.max!.y) / 2, (this.min!.z + this.max!.z) / 2)
        }
        return this.center
    }

    calculateFaceCentroids() {
        this.faceCentroids = [];
        for (let f = 0; f < this.faces.length; f++) {
            let x = 0;
            let y = 0;
            let z = 0;
            for (let v = 0; v < this.faces[f].vertexIndices.length; v++) {
                x += this.vertices[this.faces[f].vertexIndices[v]].position.x;
                y += this.vertices[this.faces[f].vertexIndices[v]].position.y;
                z += this.vertices[this.faces[f].vertexIndices[v]].position.z;
            }
            x = x / this.faces[f].vertexIndices.length;
            y = y / this.faces[f].vertexIndices.length;
            z = z / this.faces[f].vertexIndices.length;
            const centroid = new PointVector3D(x,y,z);
            this.faceCentroids.push(centroid);
        }
    }

    calculateFaceAreasAndNormals(normalCheck = true) {
        const fNormals = [];
        const fAreas = [];
        for (let f = 0; f < this.faces.length; f++) {
            const pts = [];
            let normal;
            let area;
            for (let v = 0; v < this.faces[f].vertexIndices.length; v++) {
                const pt = this.vertices[this.faces[f].vertexIndices[v]].position.copy();
                pts.push(pt);
            }
            if (pts.length == 3) {
                pts[1].subtract(pts[0]);
                pts[2].subtract(pts[0]);
                pts[1].cross(pts[2]);
                normal = pts[1].copy();
                normal.normalize();
                area = normal.magnitude / 2;
            }
            else {
                pts.push(pts[1].copy());
                pts.push(pts[2].copy());
                pts[1].subtract(pts[0]);
                pts[2].subtract(pts[0]);
                const n1 = pts[1].cross(pts[2]);
                pts[3].subtract(pts[5]);
                pts[4].subtract(pts[5]);
                const n2 = pts[3].cross(pts[4]);
                area = (n1.magnitude + n2.magnitude) / 2
                normal = new PointVector3D((n1.x + n2.x) / 2, (n1.y + n2.y) / 2, (n1.z + n2.z) / 2);
                normal.normalize();
            }
            for (let p = 0; p < pts.length; p++) {
                delete pts[p]
            }
            fNormals.push(normal);
            fAreas.push(area);
        }
        this.faceNormals = fNormals;
        this.faceAreas = fAreas;
        if (normalCheck) {
            if (!this.center) {this.calculateCenter();}
            if (!this.faceCentroids) {this.calculateFaceCentroids();}
            for (let n = 0; n < this.faceNormals.length; n++) {
                const normal = this.faceNormals[n];
                const faceCentroid = this.faceCentroids![n]
                const toCenter = new PointVector3D(this.center!.x - faceCentroid.x, this.center!.y - faceCentroid.y, this.center!.z - faceCentroid.z);
                if (normal.angle(toCenter) < Math.PI/2) {
                    normal.reverse();
                }
            }
        }
    }

    calculateVertexNormals() {
        if (!this.faceNormals || !this.faceAreas) {
            this.calculateFaceAreasAndNormals();
        }
        const vNormals = [];
        for (let v = 0; v < this.vertices.length; v++) {
            let x = 0;
            let y = 0;
            let z = 0;
            const check = [];
            for (let f = 0; f < this.vertices[v].faceIndices.length; f++) {
                check.push(this.faceNormals![this.vertices[v].faceIndices[f]].z)
            }
            for (let f = 0; f < this.vertices[v].faceIndices.length; f++) {
                x += this.faceNormals![this.vertices[v].faceIndices[f]].x * this.faceAreas![this.vertices[v].faceIndices[f]];
                y += this.faceNormals![this.vertices[v].faceIndices[f]].y * this.faceAreas![this.vertices[v].faceIndices[f]];
                z += this.faceNormals![this.vertices[v].faceIndices[f]].z * this.faceAreas![this.vertices[v].faceIndices[f]];
            }
            const vector = new PointVector3D(x,y,z);
            vector.normalize();
            vNormals.push(vector);
        }
        this.vertexNormals = vNormals;
    }

    clone() {
        const cloneVertices = [];
        const cloneFaces = [];
        for (let v = 0; v < this.vertices.length; v++) {
            let pos = this.vertices[v].position.copy();
            cloneVertices.push(new Vertex(pos));
        }
        for (let f = 0; f < this.faces.length; f++) {
            const ind = [...this.faces[f].vertexIndices];
            cloneFaces.push(new Face(ind));
        }
        return new Mesh(cloneVertices, cloneFaces);
    }

    subdivide() {
        const numberOfFacesOriginal = this.faces.length;
        const numberOfEdgesOriginal = this.edges.length;
        const numberOfVerticesOriginal = this.vertices.length;
        const facePoints = [];
        const edgePoints = [];
        const sumOfVertexPositions = new PointVector3D(0, 0);
        const averageOfVertexPositions = new PointVector3D(0, 0);
        for (let f = 0; f < numberOfFacesOriginal; f++) {
            const face = this.faces[f];
            const numberOfVerticesInFace = face.vertexIndices.length;
            sumOfVertexPositions.setToZero();
            for (let vi = 0; vi < numberOfVerticesInFace; vi++) {
                const vertexIndex = face.vertexIndices[vi];
                const vertexPos = this.vertices[vertexIndex].position;
                sumOfVertexPositions.add(vertexPos);
            }
            averageOfVertexPositions.match(sumOfVertexPositions).divide(numberOfVerticesInFace);
            facePoints.push(averageOfVertexPositions.copy());
        }
        for (let e = 0; e < numberOfEdgesOriginal; e++) {
            const edge = this.edges[e];
            sumOfVertexPositions.setToZero();
            for (let vi = 0; vi < edge.vertexIndices.length; vi++) {
                const vertexIndex = edge.vertexIndices[vi];
                const vertexPos = this.vertices[vertexIndex].position;
                sumOfVertexPositions.add(vertexPos);
            }
            const numberOfFacesAdjacent = edge.faceIndices.length;
            for (let fi = 0; fi < numberOfFacesAdjacent; fi++) {
                const faceIndex = edge.faceIndices[fi];
                const facePoint = facePoints[faceIndex];
                sumOfVertexPositions.add(facePoint);
            }
            const numberOfVertices = edge.vertexIndices.length + numberOfFacesAdjacent;
            averageOfVertexPositions.match(sumOfVertexPositions).divide(numberOfVertices);
            edgePoints.push(averageOfVertexPositions.copy());
        }
        const edgesFromFaceToEdgePoints = [];
        for (let f = 0; f < numberOfFacesOriginal; f++) {
            const face = this.faces[f];
            const facePoint = facePoints[f];
            const numberOfEdgesInFace = face.edgeIndices.length;
            for (let ei = 0; ei < numberOfEdgesInFace; ei++) {
                const edgeIndex = face.edgeIndices[ei];
                const edgePoint = edgePoints[edgeIndex];
                const edgeFromFacePointToEdgePoint = [numberOfVerticesOriginal + numberOfEdgesOriginal + f, numberOfVerticesOriginal + edgeIndex];
                edgesFromFaceToEdgePoints.push(edgeFromFacePointToEdgePoint);
            }
        }
        const edgesFromVerticesToEdgePoints = [];
        let verticesNew = [];
        for (let v = 0; v < this.vertices.length; v++) {
            const vert = this.vertices[v];
            const vertexPos = vert.position;
            const numberOfFacesAdjacent = vert.faceIndices.length;
            const numberOfEdgesAdjacent = vert.edgeIndices.length;
            sumOfVertexPositions.setToZero();
            for (let fi = 0; fi < numberOfFacesAdjacent; fi++) {
                const faceIndex = vert.faceIndices[fi];
                const facePoint = facePoints[faceIndex];
                sumOfVertexPositions.add(facePoint);
            }
            const averageOfFacePointsAdjacent = sumOfVertexPositions.copy().divide(numberOfFacesAdjacent);
            sumOfVertexPositions.setToZero();
            for (let ei = 0; ei < numberOfEdgesAdjacent; ei++) {
                const edgeIndex = vert.edgeIndices[ei];
                const edge = this.edges[edgeIndex];
                const edgeMidpoint = edge.midpoint(this);
                sumOfVertexPositions.add(edgeMidpoint);
                const edgeFromVertexToEdgePoint = [v, numberOfVerticesOriginal + edgeIndex];
                edgesFromVerticesToEdgePoints.push(edgeFromVertexToEdgePoint);
            }
            const averageOfEdgeMidpointsAdjacent = sumOfVertexPositions.copy().divide(numberOfEdgesAdjacent);
            const vertexNewPos = vertexPos.copy().multiply(numberOfFacesAdjacent - 3).add(averageOfFacePointsAdjacent).add(averageOfEdgeMidpointsAdjacent).add(averageOfEdgeMidpointsAdjacent).divide(numberOfFacesAdjacent);
            verticesNew.push(new Vertex(vertexNewPos));
        }
        verticesNew = [...verticesNew, ...Vertex.fromPositions(edgePoints)];
        verticesNew = [...verticesNew, ...Vertex.fromPositions(facePoints)];
        const facesNew = [];
        for (let f = 0; f < numberOfFacesOriginal; f++) {
            const faceOriginal = this.faces[f];
            const facePoint = facePoints[f];
            for (let vi = 0; vi < faceOriginal.vertexIndices.length; vi++) {
                const vertexIndex = faceOriginal.vertexIndices[vi];
                const vertexOriginal = this.vertices[vertexIndex];
                const vertexNew = verticesNew[vertexIndex];
                const edgeIndicesShared = [];
                for (let ei = 0; ei < vertexOriginal.edgeIndices.length; ei++) {
                    const edgeIndex = vertexOriginal.edgeIndices[ei];
                    for (let ei2 = 0; ei2 < faceOriginal.edgeIndices.length; ei2++) {
                        const edgeIndex2 = faceOriginal.edgeIndices[ei2];
                        if (edgeIndex2 == edgeIndex) {
                            edgeIndicesShared.push(edgeIndex);
                        }
                    }
                }
                const faceNew = new Face([numberOfVerticesOriginal + numberOfEdgesOriginal + f, numberOfVerticesOriginal + edgeIndicesShared[0], vertexIndex, numberOfVerticesOriginal + edgeIndicesShared[1]]);
                facesNew.push(faceNew);
            }
        }
        return new Mesh(verticesNew, facesNew);
    }

    subdivideTimes(level: number) {
        let subdividedMesh = this.clone();
        for (let i = 0; i < level; i++) {
            subdividedMesh = subdividedMesh.subdivide();
        }
        return subdividedMesh;
    }

    sortVerticesAlongVector(vector: PointVector3D, origin: PointVector3D) {
        let minDist: number | undefined;
        this.sortVector = vector.copy();
        this.sortVector.normalize();
        this.sortVectorOrigin = origin;
        this.sortVectorClosestPoints = [];
        this.sortedVertices = [];
        this.sortedDistancies = [];
        let sortingList = [];
        for (let v = 0; v < this.vertices.length; v++) {
            let closestPoint = this.sortVector.closestPoint(this.vertices[v].position, this.sortVectorOrigin)
            this.sortVectorClosestPoints.push(closestPoint);
            let dist = this.sortVectorOrigin.dist(this.sortVectorClosestPoints[v]);
            if (!minDist || dist < minDist) {minDist = dist;}
            sortingList.push({vertexNumber: v, vertexDistance: dist});
        }
        sortingList = sortingList.sort(function(a, b){return a.vertexDistance - b.vertexDistance});
        for (let v = 0; v < sortingList.length; v++) {
            this.sortedVertices.push(sortingList[v].vertexNumber);
            this.sortedDistancies.push(sortingList[v].vertexDistance - minDist!);
        }
    }

    twist(start: number, end: number, angle: number, vector = new PointVector3D(1, 0, 0), origin = new PointVector3D(0, 0, 0)) {
        if (!this.sortedVertices) {this.sortVerticesAlongVector(vector, origin);}
        const min = this.sortedDistancies![0];
        const max = this.sortedDistancies![this.sortedDistancies!.length-1];
        const s = (max - min) * start + min;
        const e = (max - min) * end + min;
        const len = e - s;
        for (let v = 0; v < this.sortedVertices!.length; v++) {
            if (this.sortedDistancies![v] < s) {continue;}
            else if (this.sortedDistancies![v] < e) {
                this.vertices[this.sortedVertices![v]].rotate(this.sortVector!, angle * ((this.sortedDistancies![v] - min - s)/len), this.sortVectorClosestPoints![this.sortedVertices![v]]);
            }
            else {
                this.vertices[this.sortedVertices![v]].rotate(this.sortVector!, angle, this.sortVectorClosestPoints![this.sortedVertices![v]]);
            }
        }
    }

    get positionsBuffer() {
        const positions = [];
        for (let v = 0; v < this.vertices.length; v++) {
            positions.push(this.vertices[v].position.x, this.vertices[v].position.y, this.vertices[v].position.z);
        }
        return positions;
    }

    get uniquePositionsBuffer() {
        const positions = [];
        for (let f = 0; f < this.faces.length; f++) {
            if (this.faces[f].vertexIndices.length == 3) {
                const faceVertex1Index = this.faces[f].vertexIndices[0];
                const faceVertex2Index = this.faces[f].vertexIndices[1];
                const faceVertex3Index = this.faces[f].vertexIndices[2];
                positions.push(this.vertices[faceVertex1Index].position.x, this.vertices[faceVertex1Index].position.y, this.vertices[faceVertex1Index].position.z);
                positions.push(this.vertices[faceVertex2Index].position.x, this.vertices[faceVertex2Index].position.y, this.vertices[faceVertex2Index].position.z);
                positions.push(this.vertices[faceVertex3Index].position.x, this.vertices[faceVertex3Index].position.y, this.vertices[faceVertex3Index].position.z);
            }
            else if (this.faces[f].vertexIndices.length == 4) {
                const faceVertex1Index = this.faces[f].vertexIndices[0];
                const faceVertex2Index = this.faces[f].vertexIndices[1];
                const faceVertex3Index = this.faces[f].vertexIndices[2];
                const faceVertex4Index = this.faces[f].vertexIndices[3];
                positions.push(this.vertices[faceVertex1Index].position.x, this.vertices[faceVertex1Index].position.y, this.vertices[faceVertex1Index].position.z);
                positions.push(this.vertices[faceVertex3Index].position.x, this.vertices[faceVertex3Index].position.y, this.vertices[faceVertex3Index].position.z);
                positions.push(this.vertices[faceVertex2Index].position.x, this.vertices[faceVertex2Index].position.y, this.vertices[faceVertex2Index].position.z);
                positions.push(this.vertices[faceVertex3Index].position.x, this.vertices[faceVertex3Index].position.y, this.vertices[faceVertex3Index].position.z);
                positions.push(this.vertices[faceVertex1Index].position.x, this.vertices[faceVertex1Index].position.y, this.vertices[faceVertex1Index].position.z);
                positions.push(this.vertices[faceVertex4Index].position.x, this.vertices[faceVertex4Index].position.y, this.vertices[faceVertex4Index].position.z);
            }
        }
        return positions;
    }

    get indicesBuffer() {
        const indices = [];
        for (let f = 0; f < this.faces.length; f++) {
            if (this.faces[f].vertexIndices.length == 3) {
                indices.push(this.faces[f].vertexIndices[0], this.faces[f].vertexIndices[1], this.faces[f].vertexIndices[2])
            }
            else if (this.faces[f].vertexIndices.length == 4) {
                indices.push(this.faces[f].vertexIndices[0], this.faces[f].vertexIndices[1], this.faces[f].vertexIndices[2])
                indices.push(this.faces[f].vertexIndices[2], this.faces[f].vertexIndices[3], this.faces[f].vertexIndices[0])
            }
        }
        return indices;
    }

    get uniqueIndicesBuffer() {
        const indices = [];
        for (let f = 0; f < this.faces.length; f++) {
            if (this.faces[f].vertexIndices.length == 3) {
                indices.push(f * 3 + 0, f * 3 + 1, f * 3 + 2)
            }
            else if (this.faces[f].vertexIndices.length == 4) {
                indices.push(f * 6 + 0, f * 6 + 1, f * 6 + 2)
                indices.push(f * 6 + 3, f * 6 + 4, f * 6 + 5)
            }
        }
        return indices;
    }

    get vertexNormalsBuffer() {
        if (!this.vertexNormals) {this.calculateVertexNormals();}
        const vertexNormals = [];
        for (let n = 0; n < this.vertexNormals!.length; n++) {
            vertexNormals.push(this.vertexNormals![n].x, this.vertexNormals![n].y, this.vertexNormals![n].z)
        }
        return vertexNormals;
    }

    get uniqueVertexNormalsBuffer() {
        if (!this.vertexNormals) {this.calculateVertexNormals();}
        const vertexNormals = [];
        for (let f = 0; f < this.faces.length; f++) {
            if (this.faces[f].vertexIndices.length == 3) {
                const faceVertex1Index = this.faces[f].vertexIndices[0];
                const faceVertex2Index = this.faces[f].vertexIndices[1];
                const faceVertex3Index = this.faces[f].vertexIndices[2];
                vertexNormals.push(this.vertexNormals![faceVertex1Index].x, this.vertexNormals![faceVertex1Index].y, this.vertexNormals![faceVertex1Index].z);
                vertexNormals.push(this.vertexNormals![faceVertex2Index].x, this.vertexNormals![faceVertex2Index].y, this.vertexNormals![faceVertex2Index].z);
                vertexNormals.push(this.vertexNormals![faceVertex3Index].x, this.vertexNormals![faceVertex3Index].y, this.vertexNormals![faceVertex3Index].z);
            }
            else if (this.faces[f].vertexIndices.length == 4) {
                const faceVertex1Index = this.faces[f].vertexIndices[0];
                const faceVertex2Index = this.faces[f].vertexIndices[1];
                const faceVertex3Index = this.faces[f].vertexIndices[2];
                const faceVertex4Index = this.faces[f].vertexIndices[3];
                vertexNormals.push(this.vertexNormals![faceVertex1Index].x, this.vertexNormals![faceVertex1Index].y, this.vertexNormals![faceVertex1Index].z);
                vertexNormals.push(this.vertexNormals![faceVertex3Index].x, this.vertexNormals![faceVertex3Index].y, this.vertexNormals![faceVertex3Index].z);
                vertexNormals.push(this.vertexNormals![faceVertex2Index].x, this.vertexNormals![faceVertex2Index].y, this.vertexNormals![faceVertex2Index].z);
                vertexNormals.push(this.vertexNormals![faceVertex3Index].x, this.vertexNormals![faceVertex3Index].y, this.vertexNormals![faceVertex3Index].z);
                vertexNormals.push(this.vertexNormals![faceVertex1Index].x, this.vertexNormals![faceVertex1Index].y, this.vertexNormals![faceVertex1Index].z);
                vertexNormals.push(this.vertexNormals![faceVertex4Index].x, this.vertexNormals![faceVertex4Index].y, this.vertexNormals![faceVertex4Index].z);
            }
        }
        return vertexNormals;
    }

    get texCoordsBuffer() {
        const texCoords = [];
        for (let f = 0; f < this.faces.length; f++) {
            if (this.faces[f].vertexIndices.length == 3) {
                texCoords.push(0, 0, 0);
                texCoords.push(0, 1, 0);
                texCoords.push(1, 1, 0);
            }
            else if (this.faces[f].vertexIndices.length == 4) {
                texCoords.push(0, 0, 0);
                texCoords.push(1, 1, 0);
                texCoords.push(1, 0, 0);
                texCoords.push(1, 1, 0);
                texCoords.push(0, 0, 0);
                texCoords.push(0, 1, 0);
            }
        }
        return texCoords;
    }

    getLinePositionsBuffer(lineWidth: number, zOffset: number = 0) {
        const positions = [];
        for (let e = 0; e < this.edges.length; e++) {
            const vertexPositions = this.edges[e].vertexPositions(this);
            const eNormal = vertexPositions[0].copy();
            eNormal.subtract(vertexPositions[1]);
            const fNormal = this.edges[e].normal(this);
            fNormal.cross(eNormal);
            fNormal.normalize();
            fNormal.multiply(lineWidth/2);
            const p1 = vertexPositions[0].copy();
            const p2 = vertexPositions[1].copy();
            const p3 = vertexPositions[1].copy();
            const p4 = vertexPositions[0].copy();
            p1.add(fNormal);
            p2.add(fNormal);
            fNormal.reverse();
            p3.add(fNormal);
            p4.add(fNormal);
            if(zOffset != 0) {
                const fNormal = this.edges[e].normal(this);
                fNormal.multiply(zOffset);
                p1.add(fNormal);
                p2.add(fNormal);
                p3.add(fNormal);
                p4.add(fNormal);
            }
            positions.push(p1.x,p1.y,p1.z,p2.x,p2.y,p2.z,p3.x,p3.y,p3.z,p4.x,p4.y,p4.z);
        }
        return positions;
    }

    get lineIndicesBuffer() {
        let indices: Array<number> = [];
        for (let e = 0; e < this.edges.length; e++) {
            indices = [...indices, e*4, e*4 + 1, e*4 + 2];
            indices = [...indices, e*4 + 2, e*4 + 3, e*4];
        }
        return indices;
    }

    get lineNormalsBuffer() {
        var lineNormals = [];
        for (let e = 0; e < this.edges.length; e++) {
            for (let v = 0; v < 4; v++) {
                lineNormals.push(this.edges[e].normal(this).x, this.edges[e].normal(this).y, this.edges[e].normal(this).z);
            }
        }
        return lineNormals;
    }

    get lineBarycentricCoordsBuffer() {
        const barycentricCoords = [];
        for (let e = 0; e < this.edges.length; e++) {
            barycentricCoords.push(1, 0, 0);
            barycentricCoords.push(0, 1, 0);
            barycentricCoords.push(0, 0, 1);
            barycentricCoords.push(1, 0, 0);
            barycentricCoords.push(0, 1, 0);
            barycentricCoords.push(0, 0, 1);
        }
        return barycentricCoords;
    }
}